<style scoped>
.search-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	flex-wrap: wrap;
}

.search-header h3 {
	margin-bottom: 0;
	white-space: nowrap;
}

.search-wrapper {
	flex: 1;
	max-width: 500px;
	min-width: 250px;
}

@media (max-width: 768px) {
	.search-header {
		flex-direction: column;
		align-items: stretch;
	}
	.search-wrapper {
		max-width: 100%;
	}
}
</style>

<template>
	<ThreeColumn>
		<template #header>
			<div class="search-header">
				<h3 class="mb-1">Communication Workbench</h3>
				<div class="d-flex align-items-center gap-2">
					<button
						v-if="!showPhoneForm"
						class="btn btn-sm btn-primary"
						@click="showPhoneForm = true"
						title="Begin phone report"
					>
						<i class="bi bi-telephone-plus"></i>
					</button>
					<div class="search-wrapper">
						<CommunicationSearch
							@clear="handleSearchClear"
							@search="handleSearchFilter"
							@select="handleSearchSelect"
						/>
					</div>
				</div>
			</div>
			<div class="text-muted small mt-1">
				Communications from various sources come in at the left, are
				investigated in the center, and labeled as valuable signal or invalid on
				the right.
			</div>
		</template>
		<template #left>
			<CommunicationColumnList
				:all="visibleCommunications"
				:closedLoading="closedLoading"
				@deselect="handleDeselect"
				:loading="storeResource.communication.loadingAll()"
				:searchFilter="searchFilter"
				:selectedID="selectedId"
				@requestClosed="fetchClosed"
				@select="handleSelect"
			/>
		</template>
		<template #center>
			<PhoneReportForm
				v-if="showPhoneForm"
				@cancel="showPhoneForm = false"
				@created="onPhoneReportCreated"
			/>
			<CommunicationColumnDetail
				v-else
				:loading="loadingSelectedCommunication"
				:mapBounds="mapBounds || undefined"
				:mapMarkers="mapMarkers"
				:property="property"
				:propertyError="propertyError"
				:propertyLoading="propertyLoading"
				:selectedCommunication="selectedCommunication"
				@viewImage="openImageViewer"
				@zoomProperty="propertyZoomBounds = $event"
			/>
		</template>
		<template #right>
			<CommunicationColumnAction
				:isLoading="loadingSelectedCommunication"
				@markInvalid="markInvalid"
				@markOpened="markOpened"
				@markPendingResponse="markPendingResponse"
				@markPossibleIssue="markPossibleIssue"
				@markPossibleResolved="markPossibleResolved"
				@sendMessage="sendMessage"
				@sendMosquitoCheck="sendMosquitoCheck"
				@copyMosquitoCheckLink="copyMosquitoCheckLink"
				@transfer="showTransferModal = true"
				:selectedCommunication="selectedCommunication"
			/>
		</template>
	</ThreeColumn>
	<ImageViewerModal
		@close="showImageModal = false"
		@imageNext="imageNext()"
		@imagePrevious="imagePrevious()"
		:images="currentImages"
		:currentImageIndex="currentImageIndex"
		:show="showImageModal"
	/>
	<CommunicationTransferModal
		@close="showTransferModal = false"
		@transferred="onTransferred"
		:communicationID="selectedId || ''"
		:show="showTransferModal"
	/>
	<MosquitoCheckSendModal
		@close="showMosquitoCheckModal = false"
		@sent="onMosquitoCheckSent"
		:communicationURI="selectedCommunication?.uri || ''"
		:contactName="selectedCommunication?.contact.name || ''"
		:show="showMosquitoCheckModal"
	/>
	<ToastNotification
		:message="toastMessage"
		:show="toastShow"
		:title="toastTitle"
	/>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { computedAsync } from "@vueuse/core";
import maplibregl from "maplibre-gl";

import { apiClient } from "@/client";
import CommunicationColumnAction from "@/components/CommunicationColumnAction.vue";
import CommunicationColumnDetail from "@/components/CommunicationColumnDetail.vue";
import CommunicationColumnList from "@/components/CommunicationColumnList.vue";
import CommunicationSearch from "@/components/CommunicationSearch.vue";
import CommunicationTransferModal from "@/components/CommunicationTransferModal.vue";
import ImageViewerModal from "@/components/ImageViewerModal.vue";
import MosquitoCheckSendModal from "@/components/MosquitoCheckSendModal.vue";
import PhoneReportForm from "@/components/PhoneReportForm.vue";
import ThreeColumn from "@/components/layout/ThreeColumn.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { useQueryParam } from "@/composable/use-query-param";
import { log } from "@/log";
import { SSEManager } from "@/SSEManager";
import type { LngLatBounds } from "@/map/Map.vue";
import { boundsForServiceArea, boundsWithPadding } from "@/map/util";
import { useStoreResource } from "@/store/resource";
import { useSessionStore } from "@/store/session";
import {
	Bounds,
	Communication,
	type CommunicationDTO,
	type CommunicationProperty,
	type CommunicationSearchResult,
	type FieldseekerServiceRequest,
	type Image,
	PublicReport,
} from "@/type/api";
import type { Marker } from "@/types";

const session = useSessionStore();

// Search state
const searchFilter = ref<string>("");
const searchZoomState = ref<{
	latitude: number;
	longitude: number;
} | null>(null);

function handleSearchClear() {
	searchFilter.value = "";
	searchZoomState.value = null;
}

function handleSearchFilter(query: string) {
	searchFilter.value = query;
}

function handleSearchSelect(result: CommunicationSearchResult) {
	searchFilter.value = ""; // Don't filter list when selecting from autocomplete
	if (
		result.location &&
		result.location.latitude != null &&
		result.location.longitude != null
	) {
		// Zoom the map to this location (several mile radius)
		searchZoomState.value = {
			latitude: result.location.latitude,
			longitude: result.location.longitude,
		};
	}
}

// Refs
const currentImageIndex = ref<number>(0);
const currentImageReportURI = ref<string>("");
const currentImageURIs = ref<Image[]>([]);
const paramCommunication = useQueryParam("communication");
const paramPhone = useQueryParam("phone");
const selectedId = ref<string | undefined>(undefined);
const showImageModal = ref(false);
const showPhoneForm = ref(false);
const showTransferModal = ref(false);
const showMosquitoCheckModal = ref(false);
// Read-only property record for the selected communication, fetched from its
// parcel_uri. propertyRequestSeq discards responses for a superseded
// selection; propertyZoomBounds overrides the map bounds until the selection
// changes (the Property card's "Zoom to property").
const property = ref<CommunicationProperty | null>(null);
const propertyError = ref<string>("");
const propertyLoading = ref<boolean>(false);
const propertyZoomBounds = ref<LngLatBounds | null>(null);
let propertyRequestSeq = 0;
// The list defaults to the open bucket. The closed bucket is fetched on demand
// the first time the list asks for it and then stays loaded: the store merges
// every response into byURI and re-runs each loaded query on SSE reconnect.
const closedLoaded = ref<boolean>(false);
const closedLoading = ref<boolean>(false);
const storeResource = useStoreResource();
const toastMessage = ref("");
const toastShow = ref(false);
const toastTitle = ref("");

let openedTimeout: number | undefined;
const currentImage = computed(() => {
	if (currentImageReportURI.value == "") return null;
	const resource = storeResource.publicreport.byURI.get(
		currentImageReportURI.value,
	);
	if (!resource) return null;
	return resource.images[currentImageIndex.value] ?? null;
});
const currentImages = computed(() => {
	if (currentImageReportURI.value != "") {
		const resource = storeResource.publicreport.byURI.get(
			currentImageReportURI.value,
		);
		if (!resource) return [];
		return resource.images ?? [];
	}
	return currentImageURIs.value;
});
const loadingSelectedCommunication = computed<boolean>((): boolean => {
	//return !!selectedCommunication.value;
	return false;
});
const mapBounds = computed<LngLatBounds | null>((): LngLatBounds | null => {
	// "Zoom to property" from the property card wins over every derived bound
	// until the selected communication changes.
	if (propertyZoomBounds.value != null) {
		return propertyZoomBounds.value;
	}
	// If a search result location is active, zoom to that with a neighborhood radius
	if (searchZoomState.value != null) {
		const srchBounds = new Bounds();
		srchBounds.addLocation({
			latitude: searchZoomState.value.latitude - 0.03,
			longitude: searchZoomState.value.longitude - 0.03,
		});
		srchBounds.addLocation({
			latitude: searchZoomState.value.latitude + 0.03,
			longitude: searchZoomState.value.longitude + 0.03,
		});
		return boundsWithPadding(srchBounds.min, srchBounds.max, 0.01);
	}

	let bounds = new Bounds();
	const reports = relatedReports(selectedCommunication.value);
	reports.forEach((r: PublicReport) => {
		if (r.location && r.location.latitude != 0 && r.location.longitude != 0) {
			bounds.addLocation(r.location);
		}
		if (
			r.address.location &&
			r.address.location.latitude != 0 &&
			r.address.location.longitude != 0
		) {
			bounds.addLocation(r.address.location);
		}
		for (const [i, image] of (r.images ?? []).entries()) {
			if (
				image.location != null &&
				image.location.latitude != 0 &&
				image.location.longitude != 0
			) {
				bounds.addLocation(image.location);
			}
		}
	});
	const serviceRequest = selectedServiceRequest(selectedCommunication.value);
	if (
		serviceRequest?.location &&
		(serviceRequest.location.latitude != 0 ||
			serviceRequest.location.longitude != 0)
	) {
		bounds.addLocation(serviceRequest.location);
	}
	if (bounds.isEmpty()) {
		return boundsForServiceArea();
	}
	return boundsWithPadding(bounds.min, bounds.max, 0.01);
});
const mapMarkers = computed<Marker[]>((): Marker[] => {
	const reports = relatedReports(selectedCommunication.value);
	let markers: Marker[] = [];
	reports.forEach((r: PublicReport) => {
		if (r.location && r.location.latitude != 0 && r.location.longitude != 0) {
			markers.push({
				color: "#0000FF",
				draggable: false,
				id: `report-location-${r.public_id}`,
				location: r.location,
			});
		}
		if (
			r.address.location &&
			r.address.location.latitude != 0 &&
			r.address.location.longitude != 0
		) {
			markers.push({
				color: "#FF0000",
				draggable: false,
				id: `report-address-${r.public_id}`,
				location: r.address.location,
			});
		}
		for (const [i, image] of (r.images ?? []).entries()) {
			if (
				image.location != null &&
				image.location.latitude != 0 &&
				image.location.longitude != 0
			) {
				markers.push({
					color: "#00FF00",
					draggable: false,
					id: `report-${r.public_id}-image-${i}`,
					location: image.location,
				});
			}
		}
	});
	const serviceRequest = selectedServiceRequest(selectedCommunication.value);
	if (
		serviceRequest?.location &&
		(serviceRequest.location.latitude != 0 ||
			serviceRequest.location.longitude != 0)
	) {
		markers.push({
			color: "#800080",
			draggable: false,
			id: `service-request-${serviceRequest.id}`,
			location: serviceRequest.location,
		});
	}
	return markers;
});
const selectedCommunication = computed((): Communication | undefined => {
	if (!selectedId.value) {
		return undefined;
	}
	const all = Array.from(storeResource.communication.byURI.values());
	const result = all.find((c: Communication) => c.id == selectedId.value);
	log.info("selectedCommunication", selectedId.value, result);
	return result;
});
const visibleCommunications = computed((): Communication[] | undefined => {
	if (!storeResource.communication.byURI) {
		return undefined;
	}
	const all: Communication[] = Array.from(
		storeResource.communication.byURI.values(),
	);
	// Apply search filter if set
	if (searchFilter.value && searchFilter.value.trim().length > 0) {
		const q = searchFilter.value.toLowerCase().trim();
		return all.filter((comm) => {
			// Check contact name
			if (comm.contact.name && comm.contact.name.toLowerCase().includes(q))
				return true;
			// Check type/source
			if (comm.type && comm.type.toLowerCase().includes(q)) return true;
			// Check ID
			if (comm.id && comm.id.toLowerCase().includes(q)) return true;
			return false;
		});
	}
	return all;
});
const handleDeselect = (id: string) => {
	selectedId.value = undefined;
};
const handleSelect = (id: string) => {
	selectedId.value = id;
	paramCommunication.setValue(id);
	maybeFetchRelatedResources();
	if (openedTimeout) {
		clearTimeout(openedTimeout);
		openedTimeout = undefined;
	}
	// Only a communication that has never been seen is marked "opened" on view.
	// Anything already opened, pending or finished must not be resurrected into
	// the open queue just by being looked at. A deep link names a communication
	// the list has not loaded yet, so its status is unknown here; the timer
	// re-reads it when it fires, by which point the list has arrived.
	const selected = selectedCommunication.value;
	if (selected && selected.status !== "new") {
		return;
	}
	openedTimeout = window.setTimeout(async () => {
		openedTimeout = undefined;
		const comm = selectedCommunication.value;
		if (comm?.status !== "new") return;
		try {
			apiClient.JSONPost(comm.urls.opened, {});
		} catch (e: any) {
			const msg =
				e instanceof Error ? e.message : "not sure what went wrong...";
			showNotification("Failed to marp opened", msg);
		}
	}, 3000);
};
function imageNext() {
	if (!currentImageIndex.value) {
		return;
	}
	currentImageIndex.value = Math.min(
		currentImages.value.length - 1,
		currentImageIndex.value + 1,
	);
}
function imagePrevious() {
	if (!currentImageIndex.value) {
		return;
	}
	currentImageIndex.value = Math.max(0, currentImageIndex.value - 1);
}
function maybeFetchRelatedResources() {
	if (!selectedId.value) {
		return;
	}
	const all = Array.from(storeResource.communication.byURI.values());
	const comm = all.find((c: Communication) => c.id == selectedId.value);
	if (!comm) return;
	// Populate the source of the communication so we can render the details
	if (comm.source) {
		storeResource.fetch(comm.source);
	}
	comm.related.forEach((r) => {
		storeResource.fetch(r.uri);
	});
}
function openImageViewer(uuid: string) {
	// Search through the various possible sources and find the matching image UUID
	log.info("show image", uuid);
	if (!selectedCommunication.value) return;
	const type_ = selectedCommunication.value.type;
	if (type_ == "public_report") {
		let source = storeResource.publicreport.byURI.get(
			selectedCommunication.value.source,
		);
		if (source) {
			for (let i = 0; i < source.images.length; i++) {
				let image = source.images[i];
				if (image.uuid == uuid) {
					log.info("image uri", image.url_content);
					currentImageIndex.value = i;
					currentImageReportURI.value = source.uri ?? "";
					currentImageURIs.value = [];
					showImageModal.value = true;
					return;
				}
			}
		}
	}
	for (let i = 0; i < selectedCommunication.value.related.length; i++) {
		let related = selectedCommunication.value.related[i];
		let related_resource = storeResource.publicreport.byURI.get(related.uri);
		if (!related_resource) continue;
		for (let j = 0; j < related_resource.images.length; j++) {
			let image = related_resource.images[j];
			if (image.uuid == uuid) {
				log.info(
					"image uri",
					image.url_content,
					j,
					related_resource.images.length,
				);
				currentImageIndex.value = j;
				currentImageReportURI.value = related.uri;
				currentImageURIs.value = [];
				showImageModal.value = true;
				return;
			}
		}
	}
	// Virtual-inspection photos (observation + off-property) are served from
	// /api/image/{uuid}/content; match the uuid against loaded inspections. The
	// viewer pages through the photos of the source the tapped photo belongs to.
	for (const inspection of storeResource.virtualinspection.byURI.values()) {
		for (const observation of inspection.observations ?? []) {
			if (observation.photos?.some((p) => p.uuid === uuid)) {
				openInspectionImage(
					uuid,
					observation.photos.map((p) => p.uuid),
				);
				return;
			}
		}
		for (const offProperty of inspection.off_property ?? []) {
			if (offProperty.photos?.some((p) => p.uuid === uuid)) {
				openInspectionImage(
					uuid,
					offProperty.photos.map((p) => p.uuid),
				);
				return;
			}
		}
	}
}
function openInspectionImage(uuid: string, photoUUIDs: string[]) {
	currentImageReportURI.value = "";
	currentImageURIs.value = photoUUIDs.map((photo_uuid) => ({
		exif: { created: "", make: "", model: "" },
		report_id: 0,
		url_content: `/api/image/${photo_uuid}/content`,
		uuid: photo_uuid,
	}));
	currentImageIndex.value = photoUUIDs.indexOf(uuid);
	showImageModal.value = true;
}

async function markInvalid() {
	markReport("Invalid", "invalid");
}
// Reopening is the one action finished work offers. The status returns to the
// open bucket, so the markReport flow's clear-selection-and-refetch is what puts
// it back in the queue the user is looking at.
function markOpened() {
	markReport("Reopen", "opened");
}
async function onPhoneReportCreated() {
	showPhoneForm.value = false;
	selectedId.value = undefined;
	await storeResource.communication.fetchAll();
	showNotification(
		"Phone Report Created",
		"The phone intake report has been submitted and added to the communications list.",
	);
}
async function onTransferred() {
	showTransferModal.value = false;
	selectedId.value = undefined;
	storeResource.communication.fetchAll();
	showNotification(
		"Communication Transferred",
		"The communication has been transferred to another district.",
	);
}

function markPendingResponse() {
	markReport("Pending Response", "pending-response");
}
async function markPossibleIssue() {
	markReport("Possible Issue", "possible-issue");
}
async function markPossibleResolved() {
	markReport("Possibly Resolved", "possible-resolved");
}
async function markReport(title: string, status: string) {
	if (selectedCommunication.value == null) {
		return;
	}
	// A communication is auto-marked "opened" 3s after it is selected. Cancel
	// that pending timer so it can't fire after we label the report here and
	// silently revert it (e.g. Invalid -> reopened -> back in the list).
	if (openedTimeout) {
		clearTimeout(openedTimeout);
		openedTimeout = undefined;
	}
	const comm = selectedCommunication.value;
	const url = `${comm.uri}/mark/${status}`;
	let response: CommunicationDTO;
	try {
		response = await apiClient.JSONPost<CommunicationDTO>(url, {});
	} catch (e: any) {
		const msg = e instanceof Error ? e.message : "Failed to update the report";
		showNotification(`Failed to Mark ${title}`, msg);
		return;
	}
	// Install the row the server just returned so the new status is visible at
	// once, without waiting on a list round trip to reach the detail pane.
	storeResource.communication.apply(Communication.fromJSON(response));
	// The status change removes this communication from the active workbench
	// queue (the list defaults to New/Opened). Clear the selection so the
	// detail/action panes don't keep showing the just-labeled report, and
	// refresh only after the server has committed so the left list reflects
	// the new status instead of a stale snapshot.
	if (selectedId.value == comm.id) {
		selectedId.value = undefined;
	}
	showNotification(
		`Report Marked ${title}`,
		`Report #${comm.id} has been updated`,
	);
	await fetchOpen();
}
function selectedServiceRequest(
	comm: Communication | undefined,
): FieldseekerServiceRequest | undefined {
	if (!comm) return undefined;
	return storeResource.servicerequest.byURI.get(comm.source);
}
function relatedReports(comm: Communication | undefined): PublicReport[] {
	let reports: PublicReport[] = [];
	if (!comm) return reports;
	if (comm.type == "public_report") {
		let source = storeResource.publicreport.byURI.get(comm.source);
		if (source) {
			reports.push(source);
		}
	}
	for (let i = 0; i < comm.related.length; i++) {
		let related = comm.related[i];
		let related_resource = storeResource.publicreport.byURI.get(related.uri);
		if (!related_resource) continue;
		reports.push(related_resource);
	}
	return reports;
}
async function sendMessage(message: string) {
	if (!message.trim()) return;
	if (selectedCommunication.value == null) return;
	if (session.urls == null) return;
	log.info("Sending message reporter:", message);
	const comm = selectedCommunication.value;
	const url = comm.urls.response;
	const response = apiClient.JSONPost(url, {
		message: message,
	});

	showNotification(
		"Message Sent",
		`Message successfully sent to ${comm.contact.name}`,
	);
}
// Communication payload URIs are path-only absolute API paths ("/api/..."):
// axios/fetch resolves them against the SPA's own origin, so sub-resources
// append directly to the URI.
function sendMosquitoCheck() {
	showMosquitoCheckModal.value = true;
}

async function onMosquitoCheckSent() {
	showMosquitoCheckModal.value = false;
	const comm = selectedCommunication.value;
	if (comm == null) return;
	showNotification(
		"Mosquito Check Sent",
		`Mosquito Check sent to ${comm.contact.name}`,
	);
	await storeResource.communication.fetchAll();
}

// Generates a Mosquito Check for the selected communication's contact WITHOUT
// sending a message, then copies the public link for delivery elsewhere.
async function copyMosquitoCheckLink() {
	const comm = selectedCommunication.value;
	if (comm == null) return;
	try {
		const resp = (await apiClient.JSONPost(`${comm.uri}/mosquito-check`, {
			send: false,
		})) as { link: string };
		await navigator.clipboard.writeText(resp.link);
		showNotification("Mosquito Check Link Copied", resp.link);
	} catch (e: any) {
		const msg =
			e instanceof Error ? e.message : "Failed to copy Mosquito Check link";
		showNotification("Mosquito Check Link Failed", msg);
	}
	await storeResource.communication.fetchAll();
}
function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;

	setTimeout(() => {
		toastShow.value = false;
	}, 3000);
}

// Load the open bucket: the workbench queue the list shows by default.
async function fetchOpen() {
	await storeResource.communication.fetchAll();
}

// The closed bucket is fetched once, when the list first asks for it (the
// operator ticks "closed"), and stays loaded afterwards. Failures are surfaced
// rather than swallowed: without the toast the closed filter would just look
// empty and the list would never ask again.
async function fetchClosed() {
	if (closedLoading.value || closedLoaded.value) {
		return;
	}
	closedLoading.value = true;
	try {
		await storeResource.communication.fetchAll("status=closed");
		closedLoaded.value = true;
	} catch (e: any) {
		const msg = e instanceof Error ? e.message : "not sure what went wrong...";
		showNotification("Failed to Load Closed Communications", msg);
	} finally {
		closedLoading.value = false;
	}
}

// Lifecycle hooks
onMounted(async () => {
	await fetchOpen();
	// At this point we may have loaded the page directly and therefore already
	// have a selectedId. If so, we want to make sure to populate its sources
	maybeFetchRelatedResources();
});
onUnmounted(() => {
	if (openedTimeout) {
		clearTimeout(openedTimeout);
	}
});
watch(
	paramCommunication.value,
	(communication_id) => {
		if (communication_id) {
			handleSelect(communication_id);
		}
	},
	{ immediate: true },
);
// "Take a call" on Home arrives as ?phone=1: open the phone-report form and
// drop the flag at once, so a refresh or a Back does not reopen it.
watch(
	paramPhone.value,
	(phone) => {
		if (phone) {
			showPhoneForm.value = true;
			paramPhone.removeValue();
		}
	},
	{ immediate: true },
);

// Fetch the selected communication's property record from its parcel_uri.
// Watching the uri (not the selection event) also covers deep links, where
// the communication arrives from the initial store load.
watch(
	() => selectedCommunication.value?.parcel_uri,
	async (parcelURI) => {
		const seq = ++propertyRequestSeq;
		property.value = null;
		propertyError.value = "";
		if (!parcelURI) {
			propertyLoading.value = false;
			return;
		}
		propertyLoading.value = true;
		try {
			const fetched = await apiClient.JSONGet<CommunicationProperty>(
				new URL(parcelURI, window.location.origin).pathname,
			);
			if (seq !== propertyRequestSeq) return;
			property.value = fetched;
		} catch {
			if (seq !== propertyRequestSeq) return;
			property.value = null;
			propertyError.value = "Could not load this communication's property.";
		} finally {
			if (seq === propertyRequestSeq) {
				propertyLoading.value = false;
			}
		}
	},
	{ immediate: true },
);

// A new selection drops any "Zoom to property" bounds so the map returns to
// the communication's own report/service-area bounds.
watch(selectedId, () => {
	propertyZoomBounds.value = null;
});
</script>
